/// ITU-R M.1677-1 Morse timing.
///
/// One "unit" is the base duration; everything else is a multiple:
///   - dit:                 1 unit
///   - dah:                 3 units
///   - intra-character gap: 1 unit  (between dit/dah within a char)
///   - inter-character gap: 3 units (between chars within a word)
///   - inter-word gap:      7 units
///
/// WPM is calibrated against the standard word "PARIS" = 50 units.
/// At W WPM the unit is `1200 / W` ms (60_000 ms / minute ÷ 50 units
/// ÷ W words/min).

export const UNIT_MS_AT_1_WPM = 1200;

export function unitMs(wpm: number): number {
  if (wpm <= 0) throw new Error("wpm must be positive");
  return UNIT_MS_AT_1_WPM / wpm;
}

export type Segment = { on: boolean; durationMs: number };

/// Convert a Morse string (output of `encode`) into a time-series of
/// on/off segments suitable for an oscillator gate or a CSS animation.
/// Adjacent same-state segments are merged so a consumer can rely on
/// the timeline alternating strictly on/off.
export function patternToTimeline(morse: string, wpm: number): Segment[] {
  const unit = unitMs(wpm);
  const out: Segment[] = [];

  // Normalise: strip whitespace around the word separator so " / "
  // is treated identically to "/", and collapse runs of inter-char
  // spaces to a single space. Without this, `encode`'s default
  // `wordSep = " / "` would over-count the gap (space + slash + space
  // each bump the off-time, ending up at 11 units instead of 7).
  const normalized = morse.replace(/\s*\/\s*/g, "/").replace(/ +/g, " ");

  // Tokenise: ".", "-", " " (intra-word char break), "/" (word
  // break). Anything else is ignored — keeps the renderer robust to
  // stray characters in user input.
  let i = 0;
  while (i < normalized.length) {
    const ch = normalized[i]!;
    if (ch === ".") {
      pushOn(out, 1 * unit);
      i += 1;
      pushOff(out, 1 * unit); // intra-character gap
    } else if (ch === "-") {
      pushOn(out, 3 * unit);
      i += 1;
      pushOff(out, 1 * unit);
    } else if (ch === " ") {
      // A bare " " is the boundary between two characters' patterns.
      // The previous tick already added a 1-unit intra-character gap;
      // we extend to 3 units total by adding 2 more.
      pushOff(out, 2 * unit);
      i += 1;
    } else if (ch === "/") {
      // Word boundary: total gap is 7 units. We've already pushed
      // 1 (intra-char), need to add 6 more.
      pushOff(out, 6 * unit);
      i += 1;
    } else {
      // Anything else — whitespace, stray characters — just skip.
      i += 1;
    }
  }

  // Strip a trailing off segment (it's a gap after the final element
  // — no point making the renderer wait for silence).
  while (out.length > 0 && !out[out.length - 1]!.on) out.pop();

  return out;
}

function pushOn(out: Segment[], durationMs: number): void {
  const last = out[out.length - 1];
  if (last && last.on) {
    last.durationMs += durationMs;
  } else {
    out.push({ on: true, durationMs });
  }
}

function pushOff(out: Segment[], durationMs: number): void {
  const last = out[out.length - 1];
  if (last && !last.on) {
    last.durationMs += durationMs;
  } else {
    out.push({ on: false, durationMs });
  }
}

/// Total runtime in milliseconds — sum of all segments.
export function totalDurationMs(segments: readonly Segment[]): number {
  let sum = 0;
  for (const s of segments) sum += s.durationMs;
  return sum;
}
